import type {ReactNode} from "react";
import {useTranslation, Trans} from "react-i18next";
import {Timeline} from "../../components/PrimereactComponents";
import ContentSection from "../../components/common/ContentSection/ContentSection";
import "./About.css";

const THESIS_KEYS = [
    "janosik", "osinek", "krejci", // 2024
    "etzler", "kodaj", "krejcir", // 2025
    "havlik", "fignar", "urbanek", "burdych" // 2026
] as const;

type Thesis = {
    key: typeof THESIS_KEYS[number];
    title: string;
    author: string;
    degree: string;
    date: string;
    url: string;
};

type TimelineEntry = {
    key: string;
    icon: string;
    dateLabel: string;
    heading?: string;
    body: ReactNode;
};

const About = () => {
    const {t, i18n} = useTranslation("about");

    const formatDate = (iso: string) =>
        new Intl.DateTimeFormat(i18n.language, {day: "numeric", month: "long", year: "numeric"}).format(new Date(`${iso}T00:00:00`));

    const theses: Thesis[] = THESIS_KEYS.map((key) => ({
        key,
        title: t(`theses.${key}.title`),
        author: t(`theses.${key}.author`),
        degree: t(`theses.${key}.degree`),
        date: t(`theses.${key}.date`),
        url: t(`theses.${key}.url`)
    }));

    const thesesByYear = theses.reduce<Record<string, Thesis[]>>((acc, thesis) => {
        const year = thesis.date.slice(0, 4);
        (acc[year] ??= []).push(thesis);
        return acc;
    }, {});

    const thesisYearEntry = (year: string): TimelineEntry => ({
        key: `theses-${year}`,
        icon: "pi pi-book",
        dateLabel: year,
        heading: t("Timeline.thesesHeading", {year}),
        body: (
            <ul className="about-thesis-list">
                {(thesesByYear[year] ?? []).map((thesis) => (
                    <li key={thesis.key}>
                        <span className="about-thesis-date">{formatDate(thesis.date)}</span>
                        <span className="about-thesis-degree">{thesis.degree}</span>
                        <a href={thesis.url} target="_blank" rel="noreferrer" className="about-thesis-title">{thesis.title}</a>
                        <span className="about-thesis-author">{thesis.author}</span>
                    </li>
                ))}
            </ul>
        )
    });

    const timelineEntries: TimelineEntry[] = [
        {
            key: "origin",
            icon: "pi pi-flag-fill",
            dateLabel: t("Timeline.originYear"),
            heading: t("Timeline.originHeading"),
            body: (
                <p>
                    <Trans
                        ns="about"
                        i18nKey="teamOrigin"
                        components={{
                            kristyna: <a href={t('links.kristyna')} target="_blank" rel="noreferrer" />,
                            jiri: <a href={t('links.jiri')} target="_blank" rel="noreferrer" />,
                            petr: <a href={t('links.petr')} target="_blank" rel="noreferrer" />
                        }}
                    />
                </p>
            )
        },
        thesisYearEntry("2024"),
        thesisYearEntry("2025"),
        {
            key: "hynek",
            icon: "pi pi-users",
            dateLabel: t("Timeline.hynekYear"),
            body: <p>{t("hynekNote")}</p>
        },
        thesisYearEntry("2026"),
        {
            key: "now",
            icon: "pi pi-compass",
            dateLabel: t("Timeline.nowLabel"),
            heading: t("Timeline.nowHeading"),
            body: <p>{t("Timeline.nowText")}</p>
        }
    ];

    return (
        <div className="page-sections">
            <ContentSection title={t("about")} level={1}>
                <p>{t("Timeline.lead", {originYear: t("Timeline.originYear"), hynekYear: t("Timeline.hynekYear")})}</p>
            </ContentSection>

            <Timeline
                value={timelineEntries}
                align="alternate"
                className="about-timeline"
                marker={(entry: TimelineEntry) => (
                    <span className="about-timeline-marker">
                        <i className={entry.icon} />
                    </span>
                )}
                content={(entry: TimelineEntry) => (
                    <ContentSection title={entry.heading} subtitle={entry.dateLabel} level={3}>
                        {entry.body}
                    </ContentSection>
                )}
            />
        </div>
    );
};

export {About};
